/**
 * Spacing and layout — aligned with UserProfile (`fieldRow` / `fieldColumn` / `contentWrapper`).
 * Use these instead of numeric literals in form screens.
 *
 * Design-token aliases (`xs`–`xl`) support shared table layout (View Requests #946); see `layout.viewRequests`.
 * Figma MCP (optional): `FIGMA_DESIGN_FILE_KEY` in `.env.local` — `_bmad-output/archive/figma-design-context.md`.
 *
 * **Visual Template** `7083:29970` (grid): fluid horizontal **24px** inset + 12 / 6 / 4 columns by
 * breakpoint. Phase 1 create-request narrow gutter uses {@link pdPagePaddingHorizontalNarrow} (**20px**).
 * **Card body** inner inset uses **`formSectionContentPaddingWeb`** (20px Stream 2 parity).
 */

import { Platform } from 'react-native';

import type { ViewStyle } from 'react-native';

const isWeb = Platform.OS === 'web';

export const spacing = {
  /** Main screen horizontal padding (UserProfile contentContainer) */
  screenPaddingHorizontal: 20,
  screenPaddingVertical: 20,

  /**
   * Centered request-form column max width on web.
   *
   * #1438 — a **derived** value, not a taste choice. It is the narrowest content width
   * the column can be offered on either side of the request-layout breakpoint, so the
   * cap binds on BOTH sides and crossing 1200 no longer changes the column width:
   *
   *   1200 (REQUEST_FORM_MOBILE_LAYOUT_MAX_WIDTH)
   *   - 252 (REQUEST_FORM_SIDEBAR_EXPANDED_WIDTH_WEB)
   *   -  40 (Request Detail page padding x2)
   *   -  15 (classic vertical scrollbar; Request Detail sets
   *          showsVerticalScrollIndicator={true}, so the scroller reserves it)
   *   = 893  -> rounded down to 880 for headroom
   *
   * Before this the cap was 1200, which only bound above ~1492px viewport, so pane width
   * passed straight through to the fields: a 1px resize across 1200 resized every field
   * by 191px, and *wider* as the window got narrower.
   *
   * The 15px scrollbar term is measured, not assumed — at viewport 1200 the step scroller
   * reports offsetWidth 948 / clientWidth 933. An earlier revision used 900 and still
   * jumped 7px on content-heavy steps for exactly this reason.
   *
   * Raising this, widening the sidebar, or lowering the breakpoint reintroduces the jump.
   * `theme/__tests__/form-max-width.test.ts` pins the invariant.
   */
  formMaxWidth: 880,

  /**
   * Visual Template grid gutter (Screens canvas metadata — see file header).
   * Phase 1 create-request flow + web two-column field rows use the same rhythm.
   */
  pdGridGutter: 24,

  /** Create Request scroll body padding (horizontal default). */
  pdPagePaddingHorizontal: 24,
  pdPagePaddingVertical: 24,
  /**
   * Create-request scroll body horizontal inset on small breakpoints (`CreateRequest` + responsive web).
   * Product target: **20px** grey gutter each side (subsection cards use {@link pdStepSubsectionGap} vertically).
   */
  pdPagePaddingHorizontalNarrow: 20,

  /**
   * Vertical gap between subsection cards on Create Request / Request Detail steps
   * (`StepForm`, `SubsectionForm`, embedded Opportunity blocks). Product target: **20px**.
   */
  pdStepSubsectionGap: 20,

  /** Gaps between columns in a two-column row on web (UserProfile fieldRow gap) */
  fieldColumnGapWeb: 24,

  /** @deprecated Prefer {@link pdStepSubsectionGap}; kept equal for legacy imports. */
  sectionMarginBottom: 20,

  /** Space between field rows (UserProfile fieldRow marginBottom) */
  fieldRowMarginBottom: 16,
  /**
   * Figma / form template: one vertical step between form **groups** (label + control to next
   * label). Matches `fieldRowMarginBottom` — `FormInput` `formGroup` should use this so spacing
   * isn’t doubled when a `fieldRow` also adds bottom margin in stacked / single-field rows.
   */
  formFieldGroupGap: 16,
  /**
   * Figma: tight space between a field and a secondary action **below** it in the same visual
   * group (e.g. stacked “Deal” row then “Link deal” on small screens), when not side-by-side.
   */
  formFieldStackedActionGap: 8,
  /**
   * Web only: slightly tighter row gap so tall steps fit above a fixed footer without extra scroll
   * (e.g. Create Request Project Details).
   */
  fieldRowMarginBottomCompactWeb: 10,

  /**
   * Web: `SubsectionForm` / `StepForm` card body horizontal + vertical padding (Stream 2 parity).
   */
  formSectionContentPaddingWeb: 20,

  /** Text “Link deal” / compact solid actions aligned to 48px control height band (template buttons). */
  formCompactActionPaddingVertical: 10,
  formCompactActionPaddingHorizontal: 16,

  /** Card inner padding (UserProfile sectionContent / card body) */
  cardPadding: 16,
  cardHeaderPadding: 16,

  pageTitleMarginBottom: 20,
  /** Space below UOM + action row before the step H1 (`StepHeader` + persisted request-detail step headers). */
  belowUomRow: 12,
  /** Space below the form step title (H1, e.g. "Opportunity", "Site Walk") before the first card — **16px**. Use for `StepHeader` and persisted step `pageTitle`; use {@link belowUomRow} only under the UOM/action row, not under the H1. */
  belowFormStepPageTitle: 16,
  /** Extra space below step H1 when the next block needs more room (e.g. Supporting Documents upload card). */
  belowFormStepPageTitleRelaxed: 24,
  uomToPageTitle: 12,
  /** Subsection line under a card title (e.g. "Requestor Information") in legacy Opportunity */
  formStepSubsectionHeadingMarginBottom: 12,
  /** Space before Project Details subsection (within Opportunity Information) */
  projectDetailsFromRequestor: 24,
  /** Tighter gap when subsection is followed by a descriptive subtitle (Project Details) */
  subsectionTitleTight: 4,
  subsectionTitleToFields: 12,
  subsectionSubtitleMarginBottom: 16,

  /** Block before Project Details card (legacy) */
  requestorSectionMarginBottom: 24,

  /**
   * Gap between field label row and its control (inputs, selects, radios). Stream 2 parity: **6px**.
   * Paired-column alignment uses {@link formInputLabelToControlOffset} (= **22**px label band + this gap).
   */
  fieldVerticalGap: 6,
  /**
   * Y-offset from the top of a one-line `FormInput` `formGroup` to the {@link formControlHeight} control:
   * **22**px label band + {@link fieldVerticalGap}. Use to align a sibling (e.g. Link deal) to the input row.
   */
  formInputLabelToControlOffset: 28,
  /** Sub-pixel trim for site vs deal+opportunity row; keep 0 unless a device shows a 1–2px drift. */
  formSitePairedRowBottomAlignFudgeWeb: 0,
  infoIconPadding: 4,
  charCountMarginTop: 4,
  /** Gap between inline error icon and message (form controls). */
  formInlineFeedbackGap: 6,
  /**
   * Single-line `TextInput` and closed dropdown trigger height (px).
   * **40** keeps dense form rows compact; stays aligned with {@link formDropdownOptionMinHeight} and radio rows.
   */
  formControlHeight: 40,
  /**
   * Vertical padding for single-line inputs/selects with {@link formControlHeight}: centers 24px control line-height
   * inside the bordered box ((40 − 2px border − 24px line) ÷ 2).
   */
  formControlSingleLinePaddingVertical: 7,
  /**
   * Border radius for text fields and select triggers (guide: 4px; use `cardBorderRadius` for cards/sections).
   */
  formControlBorderRadius: 4,
  /**
   * Horizontal inset for value text in inputs, selects, search-in-dropdown, and date fields
   * (Visual Template Input: `LG` = 12px — same numeric value as {@link spacing.md}).
   */
  formControlPaddingHorizontal: 12,
  /** Space between label text and optional trailing info icon in `FormInput`. */
  formLabelIconMarginLeft: 6,
  /** Gap between optional leading icon and value in fields with icons (Figma `md` = 8px). */
  formControlIconGap: 8,
  /**
   * `FormCheckbox` — Visual Template checkbox (`1855:7965`); see also `spacing.test.ts`.
   */
  formCheckboxOuterSize: 20,
  formCheckboxOuterDisabledSize: 16,
  formCheckboxInnerSize: 16,
  formCheckboxIndeterminateDashWidth: 10,
  formCheckboxIndeterminateDashHeight: 2,
  formCheckboxOuterRadius: 4,
  formCheckboxInnerRadius: 3,
  formCheckboxDisabledRadius: 3,
  formCheckboxUncheckedBorderWidth: 2,
  /** Horizontal gap between label text and the box (template ~10px). */
  formCheckboxLabelGap: 10,
  /** Web dropdown / native modal: minimum width of the floating options panel. */
  formDropdownMenuMinWidth: 280,
  /** Upper cap for width estimated from longest option label + subtitle. */
  formDropdownMenuMaxEstimatedWidth: 640,
  /** Viewport edge inset when positioning the web dropdown portal (`resolveWebMenuLayout`). */
  formDropdownViewportMargin: 12,
  /** Horizontal padding heuristic added to estimated text width for dropdown menu sizing. */
  formDropdownEstimateHorizontalPad: 40,
  /**
   * Visual Template — Select (`8574:33292`): `--sm` **4px** on trigger + panel + row tiles.
   */
  formDropdownBorderRadius: 4,
  /** Vertical gap between the closed trigger and the portaled menu (web). Matches `--blur/sm` **8px** in template. */
  formDropdownMenuOffsetY: 8,
  /** Web portaled menu: floor width when the trigger is narrower than this. */
  formDropdownWebMenuMinWidth: 120,
  /** Max height of the portaled / modal dropdown shell (search + options). */
  formDropdownMenuMaxHeight: 240,
  /** Max height of the scrollable option list when there is no in-menu search row. */
  formDropdownListMaxHeight: 240,
  /** Option list max height when a search row sits above the list (web portal + native modal). */
  formDropdownListMaxHeightWithSearch: 192,
  /** Vertical padding at top/bottom of the option stack inside the scroll region. */
  formDropdownListInsetVertical: 4,
  /** Single-line option row — matches {@link formControlHeight} (**40px**). */
  formDropdownOptionMinHeight: 40,
  /**
   * Two-line rows (e.g. Region code + name): slightly taller than template single-line **48** so both lines fit.
   */
  formDropdownOptionMinHeightTwoLine: 56,
  /** Vertical padding per dropdown option row (web portal + native modal); rows rely on `minHeight` + vertical centering. */
  formDropdownOptionPaddingVertical: 0,
  /** Horizontal padding per dropdown option row. */
  formDropdownOptionPaddingHorizontal: 12,
  /** Padding around the in-menu `FormSearchField` shell (web + native modal). */
  formDropdownSearchShellPadding: 8,
  /** Outer padding of the portaled menu panel (Figma `--xlg` **18px** on Select panel). */
  formDropdownMenuChromePadding: 18,
  /** Vertical gap between option tiles inside the menu (Figma `--md` **8px**). */
  formDropdownOptionRowGap: 8,
  /** Native modal list when search is shown: list `maxHeight` under the search band. */
  formDropdownModalScrollMaxHeightWithSearch: 192,
  /**
   * Trailing chevron in the closed `FormDropdown` trigger — matches legacy Opportunity form-field
   * selects (`TeamSection` layout: 12px / `#666`), not the wider template chevron.
   */
  formDropdownChevronSize: 12,
  /**
   * Closed Select trigger with primary + subtitle (`textLines={2}`). Must be ≥ {@link formControlHeight}
   * so `minHeight` / `maxHeight` stay consistent on web + native (`FormDropdown` `dropdownTwoLine`).
   */
  formDropdownTriggerTwoLineMaxHeight: 88,
  /** Symmetric vertical padding inside the two-line closed trigger band. */
  formDropdownTriggerTwoLinePaddingVertical: 8,
  /**
   * Vertical padding total used by `estimateWebMultilineHeight` (top + bottom inset around text).
   * Figma Text area (`2432:6408`): field `py` = `--blur/md` **12px** ×2 → **24px**.
   */
  formMultilineWebEstimatePaddingVertical: 24,
  /**
   * Figma Text area — inner field vertical padding (`--blur/md` = 12px per side).
   */
  formTextAreaPaddingVertical: 12,
  /**
   * Multiline / textarea default minimum height on web (resizable).
   * Product target **144px** (readable default block; still resizable on web).
   */
  formTextAreaMinHeightWeb: 144,
  /**
   * Default max height for multiline fields on web (non–project-specific).
   * Tall blocks (e.g. project description) may override with `formTextAreaMaxHeightWebTall`.
   */
  formTextAreaMaxHeightWeb: 480,
  /** Large multiline (e.g. project description) on wide layouts. */
  formTextAreaMaxHeightWebTall: 560,
  /** Multiline min height on iOS/Android (fixed `height` / min in RN). Matches {@link formTextAreaMinHeightWeb}. */
  formTextAreaMinHeightNative: 144,
  /**
   * Smallest comfortable touch target, per Apple's HIG and the Android equivalent (48dp,
   * which 44 sits under but which padding around a label typically reaches).
   *
   * Use as `minHeight` on borderless / text-style controls, where padding alone tends to
   * land around 36-38pt and passes unnoticed on a mouse-driven web review.
   */
  minimumTapTarget: 44,
  /**
   * The **floor**, not the goal: WCAG 2.2 SC 2.5.8 Target Size (Minimum), level AA, is 24x24
   * CSS px. {@link minimumTapTarget} is the comfortable size and stays the default.
   *
   * Reach for this only where a 44pt box would visibly damage the layout — a text link stacked
   * between two other rows, where 44pt centres a 16pt line and the leftover 28pt reads as a gap
   * nobody chose. When you use it, buy the area back on the axis that IS free: a right-aligned
   * link on an otherwise empty line can grow its press target leftwards for nothing.
   *
   * Do not go below it. Anything under 24 fails AA outright, and the SC's spacing exception
   * does not apply to a control stacked directly beneath another target.
   */
  minimumTapTargetCompact: 24,
  /** Tight vertical stack (title→subtitle, label→field groups) */
  contentGap: 4,
  fieldLabelEndPadding: 8,
  cardBorderRadius: 8,
  cardHeaderPaddingVertical: 14,
  /**
   * Corner radius shared by every request-toolbar action control: Location
   * Tags, PDF, View Devices, Version History and Discussion. One shape per
   * platform, following Location Tags (owner decision, 2026-09-09; before it
   * View Devices, Version History and Discussion were full 999 pills on
   * native). The values are the ones Location Tags and PDF already used: 10
   * on web's 36px control, 6 on native. "Native", not "compact": compact is
   * the chrome breakpoint and includes narrow web, which keeps the web radius.
   */
  toolbarActionBorderRadius: 10,
  toolbarActionBorderRadiusNative: 6,

  modalPadding: 24,
  modalInnerPadding: 20,
  modalMaxWidth: 400,
  /**
   * Info / neutral alert “rail” (Figma alerts – left accent on help sheets & inline callouts).
   */
  alertInfoLeftBorder: 4,

  /** Design-token scale (tables, shared layout — View Requests #946) */
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,

  /**
   * Figma Visual Template — Radio button (`Radio Button with label` / component set).
   * Outer control 16×16; label gap `md` (8px); min row height for hit target (checkbox row parity).
   */
  formRadioOuterSize: 16,
  formRadioInnerSize: 8,
  formRadioBorderWidth: 2,
  formRadioFocusBorderWidth: 3,
  /** Horizontal gap between the 16px control and the option label (`--md` in Figma). */
  formRadioLabelGap: 8,
  /** Optical top inset so the circle aligns with 14px / 22px line-height label text. */
  formRadioLabelAlignPadTop: 4,
  /** Horizontal gap between options in a horizontal group (Yes / No, Raised / Slab). */
  formRadioGroupColumnGap: 24,
  formRadioOptionMinHeight: 40,
} as const;

/** Reusable field row: two columns on web, stacked on native (UserProfile fieldRow) */
export function getFieldRowStyle() {
  return {
    flexDirection: (isWeb ? 'row' : 'column') as 'row' | 'column',
    gap: isWeb ? spacing.fieldColumnGapWeb : 0,
    marginBottom: spacing.fieldRowMarginBottom,
  };
}

/**
 * When `useTwoColumnOnWeb` is true, web uses a two-column row (gap between columns); otherwise
 * the row is a **column stack**. Use the same `formFieldGroupGap` as `FormFieldStack` so paired
 * fields (e.g. region + customer) are not 0px apart while the next stack sibling is 16px — that
 * looked like random gaps in layout overlays.
 */
export function getFieldRowStyleResponsive(useTwoColumnOnWeb: boolean) {
  const row = isWeb && useTwoColumnOnWeb;
  return {
    flexDirection: (row ? 'row' : 'column') as 'row' | 'column',
    gap: row ? spacing.fieldColumnGapWeb : spacing.formFieldGroupGap,
    marginBottom: spacing.fieldRowMarginBottom,
  };
}

export function getFieldColumnStyle() {
  return {
    flex: 1,
    marginBottom: isWeb ? 0 : spacing.fieldRowMarginBottom,
  };
}

/**
 * @param isStackedLayout — one field per row (native, or web below `mobile` breakpoint);
 *   columns get vertical spacing. Side-by-side web two-column rows pass `false`.
 *
 * Stacked: **no `flex: 1`** on column children — in a `flexDirection: 'column'` `fieldRow`,
 * equal flex growth split empty space and makes gaps between site address / deal / link look wrong.
 * Wide web: `flex: 1` so paired columns share width.
 */
export function getFieldColumnStyleResponsive(
  isStackedLayout: boolean,
): ViewStyle {
  if (isStackedLayout) {
    return {
      flexGrow: 0,
      flexShrink: 0,
      alignSelf: 'stretch',
      width: '100%',
      minWidth: 0,
      marginBottom: 0,
    };
  }
  return {
    flex: 1,
    minWidth: 0,
    marginBottom: 0,
  };
}

/**
 * Screen-specific layout constants (View All Requests web table, #946).
 */
export const layout = {
  viewRequests: {
    /** Horizontal gap between flex columns (Modified By ↔ Requestor, etc.) */
    tableColumnGap: spacing.md,
    tablePaddingVertical: spacing.lg,
    tablePaddingHorizontal: spacing.lg,
    /** Typographic cap for single-line ellipsis (`ch` on web only) */
    modifiedByMaxWidthCh: 42,
    /** Web portaled filter/sort dropdown panel (#1047) */
    dropdownMaxHeight: 300,
    dropdownScrollMaxHeight: 250,
    dropdownBorderRadius: 8,
    zIndexPortal: 999999,
  },
  /**
   * Users list table (#1452). Column widths are content measures (not xs–xl).
   * Insets alias {@link spacing.md} / {@link spacing.lg} so a token change moves this table.
   */
  users: {
    userColumnWidth: 220,
    roleColumnWidth: 96,
    dateColumnWidth: 100,
    actionColumnWidth: 80,
    tableSectionHorizontalPadding: spacing.lg,
    tableSectionVerticalPadding: spacing.md,
    tableHorizontalPadding: spacing.lg * 2,
    tableIndicatorGutter: spacing.md,
  },
  /**
   * Create Request Phase 1 steps (`StepShell` + scroll chrome). Values mirror {@link spacing}
   * PD layout tokens for a single import surface in screens.
   */
  pdPhase1: {
    contentMaxWidth: spacing.formMaxWidth,
    scrollPaddingHorizontal: spacing.pdPagePaddingHorizontal,
    scrollPaddingVertical: spacing.pdPagePaddingVertical,
    narrowScrollPaddingHorizontal: spacing.pdPagePaddingHorizontalNarrow,
    sectionCardGap: spacing.pdStepSubsectionGap,
    gridGutter: spacing.pdGridGutter,
  },
} as const;
